/**
 * Projetos — nível 1 do módulo Operação: dashboard com KPIs, tabela de
 * projetos e painéis de acompanhamento (design conforme referência
 * `integracao-nova/projetos/tela-projetos.png`).
 *
 * "Horas registradas" e "Risco" do print de referência não têm dado real no
 * backend (sem apontamento de horas nem cálculo de risco) — substituídos por
 * "Progresso médio" (derivado de tarefas concluídas/total) e "Prioridade"
 * (campo real do projeto), pra não exibir número inventado pro usuário. Pelo
 * mesmo motivo, "Carga da equipe" (capacidade em horas por pessoa) virou
 * "Projetos por responsável" (contagem real).
 *
 * "Novo projeto" abre um modal que escolhe um template (colunas + tarefas-
 * semente) ou começa em branco. Clique na linha leva ao Kanban do projeto
 * (ProjetoBoard, rota /operacao/projetos/:id).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { SearchInput } from '../../components/UX.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  pjListProjects, pjCreateProject, pjListTemplates, pjDeleteProject,
  pjDeleteTemplate, pjExportTemplate,
} from '../../db/api.js'
import {
  PRIO_BADGE, PRIO_LABEL, PRIO_ORDER, PROJECT_STATUS_COLORS, PROJECT_STATUS_LABEL,
  fmtDate, pctDone, initials, avatarColor,
} from './projetosKit.jsx'
import ClientePicker from './ClientePicker.jsx'
import ProjectMenu from './ProjectMenu.jsx'
import EditProjectModal from './EditProjectModal.jsx'
import ImportTemplateModal from './ImportTemplateModal.jsx'
import './projetos-dashboard.css'

const DAY_MS = 86400000
const TABS = [
  { id: 'todos', label: 'Todos' },
  { id: 'meus', label: 'Meus projetos' },
  { id: 'arquivados', label: 'Arquivados' },
]

// Dias até o prazo (negativo = atrasado). null se não tem prazo.
function daysUntil(prazo) {
  if (!prazo) return null
  const d = new Date(prazo)
  if (isNaN(d)) return null
  return Math.round((d.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / DAY_MS)
}

function isAtrasado(p) {
  if (!p.prazo || p.status === 'concluido' || p.status === 'arquivado') return false
  const dd = daysUntil(p.prazo)
  return dd != null && dd < 0
}

export default function Projetos() {
  const { selectedClient, confirm, toast, user } = useApp()
  const clientId = selectedClient?.id || null
  const navigate = useNavigate()

  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(null)   // projeto em edição (modal)
  const [tab, setTab] = useState('todos')
  const [showFilters, setShowFilters] = useState(false)
  const [filtStatus, setFiltStatus] = useState('')
  const [filtPrio, setFiltPrio] = useState('')
  const [filtOwner, setFiltOwner] = useState('')
  const [page, setPage] = useState(1)
  const filtersRef = useRef(null)

  useEffect(() => {
    if (!showFilters) return
    const onDoc = (e) => { if (filtersRef.current && !filtersRef.current.contains(e.target)) setShowFilters(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [showFilters])

  const load = async () => {
    setErr(null)
    try { setProjects(await pjListProjects()) }
    catch (e) { setErr(e.message) }
    finally { setLoading(false) }
  }

  useEffect(() => {
    setProjects([]); setSearch(''); setErr(null)
    if (!clientId) { setLoading(false); return }
    setLoading(true)
    let alive = true
    ;(async () => {
      try { const ps = await pjListProjects(); if (alive) setProjects(ps) }
      catch (e) { if (alive) setErr(e.message) }
      finally { if (alive) setLoading(false) }
    })()
    return () => { alive = false }
  }, [clientId])

  const removeProject = async (p) => {
    const ok = await confirm({
      title: 'Excluir projeto',
      message: `Excluir o projeto "${p.nome}"? As tarefas vinculadas também serão removidas.`,
      confirmText: 'Excluir projeto', danger: true,
    })
    if (!ok) return
    // otimista: some da lista; recarrega no erro
    setProjects((prev) => prev.filter((x) => x.id !== p.id))
    try { await pjDeleteProject(p.id) }
    catch (e2) { toast(`Erro ao excluir: ${e2.message}`, 'error'); load() }
  }

  // Escopo por aba: Todos/Meus excluem arquivados; Arquivados só eles.
  const scoped = useMemo(() => {
    if (tab === 'arquivados') return projects.filter((p) => p.status === 'arquivado')
    if (tab === 'meus') return projects.filter((p) => p.status !== 'arquivado' && p.owner_id === user?.id)
    return projects.filter((p) => p.status !== 'arquivado')
  }, [projects, tab, user?.id])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return scoped.filter((p) => {
      if (q && ![p.nome, p.deal_titulo, p.contact_nome, p.company_nome, p.owner_nome]
        .some((v) => (v || '').toLowerCase().includes(q))) return false
      if (filtStatus && p.status !== filtStatus) return false
      if (filtPrio && (p.prioridade || 'media') !== filtPrio) return false
      if (filtOwner === '__none__' && p.owner_nome) return false
      if (filtOwner && filtOwner !== '__none__' && p.owner_nome !== filtOwner) return false
      return true
    })
  }, [scoped, search, filtStatus, filtPrio, filtOwner])

  const activeFilterCount = [filtStatus, filtPrio, filtOwner].filter(Boolean).length
  const clearFilters = () => { setFiltStatus(''); setFiltPrio(''); setFiltOwner('') }

  const PAGE_SIZE = 6
  useEffect(() => { setPage(1) }, [tab, search, filtStatus, filtPrio, filtOwner])
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const page1 = Math.min(page, totalPages)
  const paged = useMemo(
    () => filtered.slice((page1 - 1) * PAGE_SIZE, page1 * PAGE_SIZE),
    [filtered, page1],
  )

  // Responsáveis distintos entre os projetos ativos — opções do filtro.
  const ownerOptions = useMemo(() => {
    const set = new Set()
    let hasNone = false
    for (const p of projects) {
      if (p.status === 'arquivado') continue
      if (p.owner_nome) set.add(p.owner_nome)
      else hasNone = true
    }
    return { names: [...set].sort(), hasNone }
  }, [projects])

  // KPIs: sempre sobre o universo não-arquivado, independente da aba/busca.
  const ativos = useMemo(() => projects.filter((p) => p.status !== 'arquivado'), [projects])
  const kpis = useMemo(() => {
    const atrasados = ativos.filter(isAtrasado)
    const concluidos = ativos.filter((p) => p.status === 'concluido')
    const emAndamento = ativos.filter((p) => p.status === 'ativo')
    const comTarefas = ativos.filter((p) => p.tasks_total > 0)
    const progressoMedio = comTarefas.length
      ? Math.round(comTarefas.reduce((s, p) => s + pctDone(p.tasks_done, p.tasks_total), 0) / comTarefas.length)
      : 0
    return { atrasados, concluidos, emAndamento, progressoMedio }
  }, [ativos])

  // Painel "Atenção esta semana": dois grupos — atrasados e prazos nos
  // próximos 7 dias (contagem real; sem "sobrecarga de equipe" porque não há
  // apontamento de horas/capacidade no backend pra sustentar esse número).
  const alertGroups = useMemo(() => {
    const atrasadosOrd = [...kpis.atrasados].sort((a, b) => new Date(a.prazo) - new Date(b.prazo))
    const proximos = ativos
      .filter((p) => !isAtrasado(p) && p.status !== 'concluido')
      .map((p) => ({ p, dd: daysUntil(p.prazo) }))
      .filter((x) => x.dd != null && x.dd <= 7)
      .sort((a, b) => a.dd - b.dd)
      .map((x) => x.p)
    const groups = []
    if (atrasadosOrd.length) {
      const [first, ...rest] = atrasadosOrd
      groups.push({
        tipo: 'atrasado',
        titulo: `${atrasadosOrd.length} projeto${atrasadosOrd.length > 1 ? 's' : ''} atrasado${atrasadosOrd.length > 1 ? 's' : ''}`,
        desc: rest.length ? `${first.nome} e mais ${rest.length} projeto${rest.length > 1 ? 's' : ''} estão com atraso.` : `${first.nome} está com atraso.`,
        target: first,
      })
    }
    if (proximos.length) {
      const [first] = proximos
      groups.push({
        tipo: 'proximo',
        titulo: `${proximos.length} prazo${proximos.length > 1 ? 's' : ''} próximo${proximos.length > 1 ? 's' : ''}`,
        desc: `${proximos.length} projeto${proximos.length > 1 ? 's' : ''} ${proximos.length > 1 ? 'vencem' : 'vence'} nos próximos 7 dias.`,
        target: first,
      })
    }
    return groups
  }, [kpis.atrasados, ativos])

  // Painel "Projetos por responsável": contagem real (não é carga em horas —
  // não há apontamento de horas no backend).
  const porResponsavel = useMemo(() => {
    const map = new Map()
    for (const p of ativos) {
      const nome = p.owner_nome || 'Sem responsável'
      map.set(nome, (map.get(nome) || 0) + 1)
    }
    const rows = [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5)
    const max = rows.reduce((m, [, n]) => Math.max(m, n), 1)
    return rows.map(([nome, count]) => ({ nome, count, pct: Math.max(8, Math.round((count / max) * 100)) }))
  }, [ativos])

  // Painel "Projetos por prioridade": todo projeto sempre tem prioridade
  // (campo obrigatório com default 'media'), então esse painel nunca fica
  // vazio — ajuda a preencher a lateral quando responsável/prazo não estão
  // preenchidos ainda.
  const porPrioridade = useMemo(() => {
    const total = ativos.length || 1
    return PRIO_ORDER.map((key) => {
      const count = ativos.filter((p) => (p.prioridade || 'media') === key).length
      return { key, count, pct: Math.round((count / total) * 100) }
    }).filter((r) => r.count > 0)
  }, [ativos])

  if (!clientId) {
    return (
      <Layout title="Projetos" breadcrumb={[{ label: 'Operação' }, { label: 'Projetos' }]}>
        <div style={S.center}>Selecione um cliente para ver os projetos.</div>
      </Layout>
    )
  }

  return (
    <Layout title="Projetos" breadcrumb={[{ label: 'Operação' }, { label: 'Projetos' }]}>
      <div className="pjd-wrap">
        <div className="pjd-toolbar">
          <div className="pjd-tabs">
            {TABS.map((t) => (
              <button key={t.id} type="button"
                className={`pjd-tab${tab === t.id ? ' is-active' : ''}`}
                onClick={() => setTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="pjd-actions" style={{ position: 'relative' }} ref={filtersRef}>
            <button type="button" className="pjd-ctrl" onClick={() => setShowFilters((v) => !v)}>
              <Icon n="sliders" s={14} sw={2} /> Filtros
              {activeFilterCount > 0 && <span className="pjd-filter-count">{activeFilterCount}</span>}
            </button>
            {showFilters && (
              <div className="pjd-search-pop">
                <SearchInput placeholder="Buscar projeto…" value={search} onChange={setSearch} />

                <label className="pjd-filter-label">Status</label>
                <select className="pjd-filter-select" value={filtStatus} onChange={(e) => setFiltStatus(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="ativo">Ativo</option>
                  <option value="pausado">Pausado</option>
                  <option value="concluido">Concluído</option>
                </select>

                <label className="pjd-filter-label">Prioridade</label>
                <select className="pjd-filter-select" value={filtPrio} onChange={(e) => setFiltPrio(e.target.value)}>
                  <option value="">Todas</option>
                  {PRIO_ORDER.map((p) => <option key={p} value={p}>{PRIO_LABEL[p]}</option>)}
                </select>

                <label className="pjd-filter-label">Responsável</label>
                <select className="pjd-filter-select" value={filtOwner} onChange={(e) => setFiltOwner(e.target.value)}>
                  <option value="">Todos</option>
                  {ownerOptions.names.map((n) => <option key={n} value={n}>{n}</option>)}
                  {ownerOptions.hasNone && <option value="__none__">Sem responsável</option>}
                </select>

                {activeFilterCount > 0 && (
                  <button type="button" className="pjd-filter-clear" onClick={clearFilters}>Limpar filtros</button>
                )}
              </div>
            )}
            <button type="button" className="pjd-ctrl primary" onClick={() => setCreating(true)}>
              <Icon n="plus" s={14} sw={2} /> Novo projeto
            </button>
          </div>
        </div>

        {err && <div style={S.error}>{err}</div>}

        <div className="pjd-dashboard">
          <section className="pjd-primary" aria-label="Visão geral dos projetos">
            {!loading && (
              <div className="pjd-kpis">
                <KpiCard icon="chart_up" tone="blue" label="Em andamento" value={kpis.emAndamento.length}
                  note={`${ativos.length ? Math.round(kpis.emAndamento.length / ativos.length * 100) : 0}% dos não arquivados`} />
                <KpiCard icon="clock" tone="orange" label="Atrasados" value={kpis.atrasados.length}
                  note={kpis.atrasados.length ? 'Precisam de atenção' : 'Nenhum prazo vencido'} />
                <KpiCard icon="check" tone="green" label="Concluídos" value={kpis.concluidos.length}
                  note={`${ativos.length ? Math.round(kpis.concluidos.length / ativos.length * 100) : 0}% dos não arquivados`} />
                <KpiCard icon="trend_up" tone="purple" label="Progresso médio" value={`${kpis.progressoMedio}%`}
                  note="Tarefas concluídas / total" />
              </div>
            )}

          <div className="pjd-card pjd-panel" style={{ padding: 0 }}>
            {loading && <div className="pjd-empty">Carregando…</div>}

            {!loading && !projects.length && (
              <div className="pjd-empty">
                <div style={{ marginBottom: 8 }}>Nenhum projeto ainda.</div>
                <div className="pjd-empty-hint">Crie um projeto a partir de um template ou em branco — para clientes ou trabalho interno.</div>
              </div>
            )}

            {!loading && projects.length > 0 && !filtered.length && (
              <div className="pjd-empty">Nenhum projeto encontrado.</div>
            )}

            {!loading && filtered.length > 0 && (
              <div className="pjd-table-scroll">
                <table className="pjd-table">
                  <thead>
                    <tr>
                      <th>Projeto</th>
                      <th>Cliente</th>
                      <th>Responsável</th>
                      <th>Status</th>
                      <th>Progresso</th>
                      <th>Tarefas</th>
                      <th>Prazo</th>
                      <th>Prioridade</th>
                      <th aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((p) => {
                      const pct = pctDone(p.tasks_done, p.tasks_total)
                      const late = isAtrasado(p)
                      // Status exibido funde saúde do projeto + atraso real (prazo
                      // vencido pesa mais que o status macro) — sem inventar campo novo.
                      const statusLabel = late ? 'Atrasado' : (PROJECT_STATUS_LABEL[p.status] || p.status)
                      const statusColor = late ? '#b54708' : (PROJECT_STATUS_COLORS[p.status] || '#667085')
                      const cliente = p.contact_nome || p.company_nome || 'Interno'
                      const prio = PRIO_BADGE[p.prioridade]
                      const dd = daysUntil(p.prazo)
                      return (
                        <tr key={p.id} className="is-row" tabIndex={0}
                          onClick={() => navigate(`/operacao/projetos/${p.id}`)}
                          onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/operacao/projetos/${p.id}`) }}>
                          <td data-label="Projeto">
                            <div className="pjd-proj-row">
                              <span className="pjd-proj-icon"><Icon n="folder" s={16} /></span>
                              <div style={{ minWidth: 0 }}>
                                <div className="pjd-proj-name" title={p.nome}>{p.nome}</div>
                                {(p.deal_titulo || p.template_key) && (
                                  <span className="pjd-proj-tag">{p.deal_titulo || p.template_key}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td data-label="Cliente">{cliente}</td>
                          <td data-label="Responsável">
                            {p.owner_nome ? (
                              <div className="pjd-who">
                                <span className="pjd-av" style={{ background: avatarColor(p.owner_id) }}>{initials(p.owner_nome)}</span>
                                <span className="pjd-who-name" title={p.owner_nome}>{p.owner_nome}</span>
                              </div>
                            ) : <span style={{ color: '#98a2b3' }}>—</span>}
                          </td>
                          <td data-label="Status">
                            <span className="pjd-pill" style={{ color: statusColor, background: `${statusColor}18` }}>
                              {statusLabel}
                            </span>
                          </td>
                          <td data-label="Progresso"><ProgressRing pct={pct} color={statusColor} /></td>
                          <td data-label="Tarefas">
                            <div className="pjd-tasks-val">{p.tasks_done}/{p.tasks_total}</div>
                            <div className="pjd-tasks-sub">concluídas</div>
                            <div className="pjd-tasks-bar"><i style={{ width: `${pct}%`, background: statusColor }} /></div>
                          </td>
                          <td data-label="Prazo">
                            {p.prazo ? (
                              <div className={`pjd-prazo${late ? ' is-late' : ''}`}>
                                <span className="pjd-prazo-ico"><Icon n="calendar" s={13} /></span>
                                <span>
                                  <span className="pjd-prazo-date">{fmtDate(p.prazo)}</span>
                                  <span className="pjd-prazo-sub">
                                    {late ? `atrasado ${Math.abs(dd)}d` : dd === 0 ? 'hoje' : `em ${dd}d`}
                                  </span>
                                </span>
                              </div>
                            ) : <span style={{ color: '#98a2b3' }}>—</span>}
                          </td>
                          <td data-label="Prioridade">
                            {prio && (
                              <span className="pjd-risk" style={{ color: prio.fg }}>
                                <i style={{ background: prio.fg }} /> {PRIO_LABEL[p.prioridade]}
                              </span>
                            )}
                          </td>
                          <td className="pjd-actions-cell" onClick={(e) => e.stopPropagation()}>
                            <div className="pjd-row-actions">
                              <ProjectMenu onEdit={() => setEditing(p)} onDelete={() => removeProject(p)} />
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && filtered.length > PAGE_SIZE && (
              <div className="pjd-pager">
                <span className="pjd-pager-info">
                  {(page1 - 1) * PAGE_SIZE + 1}–{Math.min(page1 * PAGE_SIZE, filtered.length)} de {filtered.length}
                </span>
                <div className="pjd-pager-btns">
                  <button type="button" className="pjd-ctrl" disabled={page1 <= 1} onClick={() => setPage(page1 - 1)}>
                    <Icon n="chevron_l" s={14} sw={2} />
                  </button>
                  <span className="pjd-pager-page">Página {page1} de {totalPages}</span>
                  <button type="button" className="pjd-ctrl" disabled={page1 >= totalPages} onClick={() => setPage(page1 + 1)}>
                    <Icon n="chevron_r" s={14} sw={2} />
                  </button>
                </div>
              </div>
            )}
          </div>
          </section>

          <aside className="pjd-side" aria-label="Acompanhamento dos projetos">
            <div className="pjd-card pjd-panel">
              <div className="pjd-panel-title"><Icon n="bell" s={16} c="#b54708" /> Atenção esta semana</div>
              {!alertGroups.length && <div className="pjd-empty" style={{ padding: '8px 0' }}>Nenhum alerta por aqui.</div>}
              {alertGroups.map((g) => (
                <div key={g.tipo} className="pjd-alert-item">
                  <span className={`pjd-alert-icon ${g.tipo === 'atrasado' ? 'is-warn' : 'is-info'}`}>
                    <Icon n={g.tipo === 'atrasado' ? 'clock' : 'calendar'} s={16} />
                  </span>
                  <span className="pjd-alert-text">
                    <span className="pjd-alert-head">{g.titulo}</span>
                    <span className="pjd-alert-sub">{g.desc}</span>
                    <button type="button" className="pjd-alert-link" onClick={() => navigate(`/operacao/projetos/${g.target.id}`)}>
                      Ver projeto <Icon n="arrow_right" s={12} sw={2} />
                    </button>
                  </span>
                </div>
              ))}
            </div>

            <div className="pjd-card pjd-panel">
              <div className="pjd-panel-head">
                <div className="pjd-panel-title"><Icon n="users" s={16} c="#2563eb" /> Projetos por responsável</div>
                <span className="pjd-period">Ativos</span>
              </div>
              {!porResponsavel.length && <div className="pjd-empty" style={{ padding: '8px 0' }}>Sem projetos ativos.</div>}
              {porResponsavel.length > 0 && (
                <div className="pjd-chart">
                  <div className="pjd-chart-scale" aria-hidden="true">
                    <span>{porResponsavel[0].count}</span>
                    <span>{Math.ceil(porResponsavel[0].count / 2)}</span>
                    <span>0</span>
                  </div>
                  <div className="pjd-bars">
                    {porResponsavel.map((r) => (
                      <div key={r.nome} className="pjd-bar-col" title={`${r.nome}: ${r.count} projeto${r.count > 1 ? 's' : ''}`}>
                        <span className="pjd-bar-count">{r.count}</span>
                        <div className="pjd-bar-col-track">
                          <div className="pjd-bar-col-fill" style={{ height: `${r.pct}%` }} />
                        </div>
                        <span className="pjd-bar-col-name">{r.nome.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="pjd-card pjd-panel">
              <div className="pjd-panel-title"><Icon n="chart" s={16} c="#7c5cf0" /> Projetos por prioridade</div>
              {!porPrioridade.length && <div className="pjd-empty" style={{ padding: '8px 0' }}>Sem projetos ativos.</div>}
              {porPrioridade.map((r) => {
                const badge = PRIO_BADGE[r.key]
                return (
                  <div key={r.key} className="pjd-prio-row">
                    <span className="pjd-prio-name"><i style={{ background: badge?.fg }} /> {PRIO_LABEL[r.key]}</span>
                    <div className="pjd-prio-track"><div className="pjd-prio-fill" style={{ width: `${r.pct}%`, background: badge?.fg }} /></div>
                    <span className="pjd-prio-count">{r.count}</span>
                  </div>
                )
              })}
            </div>
          </aside>
        </div>
      </div>

      {creating && (
        <NovoProjetoModal
          onClose={() => setCreating(false)}
          onCreated={(proj) => { setCreating(false); navigate(`/operacao/projetos/${proj.id}`) }}
        />
      )}

      {editing && (
        <EditProjectModal
          project={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null)
            setProjects((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))
          }}
        />
      )}
    </Layout>
  )
}

// ── Modal: novo projeto (template + nome + prioridade + prazo) ──────────────
export function NovoProjetoModal({ onClose, onCreated }) {
  const { confirm, toast } = useApp()
  const [templates, setTemplates] = useState([])
  const [templateKey, setTemplateKey] = useState('vazio')
  const [nome, setNome] = useState('')
  const [prioridade, setPrioridade] = useState('media')
  const [prazo, setPrazo] = useState('')
  const [cliente, setCliente] = useState(null)   // { id, nome } | null
  const [showImport, setShowImport] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const loadTemplates = () => pjListTemplates().then(setTemplates).catch(() => {})
  useEffect(() => { loadTemplates() }, [])

  const selected = templates.find((t) => t.key === templateKey)

  const submit = async () => {
    if (!nome.trim() || saving) return
    setSaving(true); setErr(null)
    try {
      const proj = await pjCreateProject({
        nome: nome.trim(), template_key: templateKey, prioridade,
        prazo: prazo || null, contact_id: cliente?.id || null,
      })
      onCreated(proj)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const exportTpl = async (t) => {
    try {
      const data = await pjExportTemplate(t.id)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `template-${(t.label || 'projeto').toLowerCase().replace(/\s+/g, '-')}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) { toast(e.message, 'error') }
  }

  const deleteTpl = async (t) => {
    const ok = await confirm({
      title: 'Excluir template', message: `Excluir o template "${t.label}"?`,
      confirmText: 'Excluir', danger: true,
    })
    if (!ok) return
    try {
      await pjDeleteTemplate(t.id)
      if (templateKey === t.key) setTemplateKey('vazio')
      loadTemplates()
    } catch (e) { toast(e.message, 'error') }
  }

  return (
    <div style={S.mOverlay} onMouseDown={onClose}>
      <div style={S.mBox} role="dialog" aria-modal="true" aria-label="Novo projeto" onMouseDown={(e) => e.stopPropagation()}>
        <div style={S.mHead}>
          <h4 style={S.mTitle}>Novo projeto</h4>
          <button style={S.mX} onClick={onClose}>×</button>
        </div>
        {err && <div style={S.error}>{err}</div>}

        <input style={S.input} placeholder="Nome do projeto" value={nome} autoFocus
          onChange={(e) => setNome(e.target.value)} />

        <label style={S.miniLabel}>Cliente (opcional)</label>
        <ClientePicker value={cliente} onChange={setCliente} />

        <div style={S.tplHeadRow}>
          <label style={{ ...S.miniLabel, margin: 0 }}>Template</label>
          <button style={S.importBtn} onClick={() => setShowImport(true)}>
            <Icon n="download" s={12} /> Importar template
          </button>
        </div>
        <div style={S.tplGrid}>
          {templates.map((t) => {
            const active = t.key === templateKey
            return (
              <div key={t.key} role="button" tabIndex={0}
                style={{ ...S.tplCard, ...(active ? S.tplCardActive : null) }}
                onClick={() => setTemplateKey(t.key)}
                onKeyDown={(e) => { if (e.key === 'Enter') setTemplateKey(t.key) }}>
                <div style={S.tplLabel}>
                  {t.label}
                  {t.origin !== 'builtin' && <span style={S.tplTag}>custom</span>}
                </div>
                <div style={S.tplDesc}>{t.descricao}</div>
                <div style={S.tplMeta}>{t.columns.length} colunas · {t.task_count} tarefas</div>
                {t.deletable && (
                  <div style={S.tplActions}>
                    <button style={S.tplActBtn} title="Exportar JSON"
                      onClick={(e) => { e.stopPropagation(); exportTpl(t) }}>
                      <Icon n="download" s={12} />
                    </button>
                    <button style={S.tplActBtn} title="Excluir template"
                      onClick={(e) => { e.stopPropagation(); deleteTpl(t) }}>
                      <Icon n="x" s={12} sw={2} />
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {selected && (
          <div style={S.tplCols}>
            {selected.columns.map((c, i) => (
              <span key={i} style={S.tplColChip}>{c}</span>
            ))}
          </div>
        )}

        <div style={S.addGrid}>
          <div>
            <label style={S.miniLabel}>Prioridade</label>
            <select style={S.input} value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
              {PRIO_ORDER.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label style={S.miniLabel}>Prazo</label>
            <input style={S.input} type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
          </div>
        </div>

        <div style={S.addFooter}>
          <button type="button" className="pjd-ctrl" onClick={onClose}>Cancelar</button>
          <button type="button" className="pjd-ctrl primary" onClick={submit} disabled={saving || !nome.trim()}>
            {saving ? 'Criando…' : 'Criar projeto'}
          </button>
        </div>
      </div>

      {showImport && (
        <ImportTemplateModal
          onClose={() => setShowImport(false)}
          onImported={(t) => { setShowImport(false); loadTemplates().then(() => setTemplateKey(t.id)) }}
        />
      )}
    </div>
  )
}

// Card de KPI do topo (Em andamento / Atrasados / Concluídos / Progresso médio).
function KpiCard({ icon, tone, label, value, note }) {
  return (
    <div className="pjd-card pjd-kpi">
      <div className="pjd-kpi-top">
        <span className={`pjd-kpi-icon is-${tone}`}>
          <Icon n={icon} s={19} sw={1.7} />
        </span>
        <div>
          <div className="pjd-kpi-value">{value}</div>
          <div className="pjd-kpi-label">{label}</div>
        </div>
      </div>
      {note && <div className="pjd-kpi-note">{note}</div>}
    </div>
  )
}

// Anel de progresso (coluna "Progresso" da tabela) — SVG leve, sem lib de
// gráfico (só um círculo por linha, recharts seria overkill aqui).
function ProgressRing({ pct, color = '#2563eb', size = 54, stroke = 4 }) {
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const offset = c * (1 - Math.min(100, Math.max(0, pct)) / 100)
  return (
    <div className="pjd-ring-wrap" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f2f4f7" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <span className="pjd-ring-pct">{pct}%</span>
    </div>
  )
}

const S = {
  center: { color: 'var(--sec)', fontSize: 14, padding: '48px 16px', textAlign: 'center' },
  error: {
    padding: '10px 14px', borderRadius: 10, background: '#fef6f5',
    border: '1px solid #fbd9d3', color: '#b42318', marginBottom: 12, fontSize: 13,
  },

  // modal
  mOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(16,24,40,.35)', zIndex: 1200,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  mBox: {
    width: 560, maxWidth: '100%', background: 'var(--surface)', border: '1px solid #eaecf0',
    borderRadius: 14, padding: 20, boxShadow: '0 24px 48px rgba(16,24,40,.24)',
    display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '86vh', overflowY: 'auto',
  },
  mHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  mTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: '#101828' },
  mX: { border: 'none', background: 'transparent', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', color: '#667085', fontSize: 20, lineHeight: 1 },
  input: {
    width: '100%', boxSizing: 'border-box', height: 38, padding: '0 12px', borderRadius: 8,
    border: '1px solid #d0d5dd', background: 'var(--surface)', color: '#101828', fontSize: 13, fontFamily: 'inherit',
  },
  miniLabel: { display: 'block', fontSize: 13, fontWeight: 500, color: '#344054', marginBottom: 6, marginTop: 10 },
  tplHeadRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 },
  importBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, border: '1px solid #d0d5dd',
    background: 'var(--surface)', color: '#344054', borderRadius: 8, padding: '0 10px',
    fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
  },
  tplGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  tplCard: {
    position: 'relative', textAlign: 'left', border: '1px solid #e4e7ec', borderRadius: 10,
    padding: '11px 12px', background: 'var(--surface)', cursor: 'pointer', display: 'flex',
    flexDirection: 'column', gap: 3,
  },
  tplCardActive: { borderColor: 'var(--blue)', boxShadow: '0 0 0 1px var(--blue)', background: '#f7faff' },
  tplLabel: { fontSize: 13.5, fontWeight: 700, color: '#101828', display: 'flex', alignItems: 'center', gap: 6 },
  tplTag: {
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: 'var(--sec)',
    background: 'var(--surface)', border: '1px solid var(--soft)', borderRadius: 100, padding: '1px 6px',
  },
  tplActions: { position: 'absolute', top: 6, right: 6, display: 'flex', gap: 2 },
  tplActBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none',
    background: 'transparent', color: 'var(--sec)', cursor: 'pointer', padding: 3, borderRadius: 6,
  },
  tplDesc: { fontSize: 12, color: '#667085', lineHeight: 1.35 },
  tplMeta: { fontSize: 11, color: '#98a2b3', marginTop: 2, fontWeight: 600 },
  tplCols: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  tplColChip: {
    fontSize: 11, fontWeight: 600, color: '#344054', background: '#f2f4f7',
    borderRadius: 100, padding: '3px 10px',
  },
  addGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  addFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14, paddingTop: 14, borderTop: '1px solid #eaecf0' },
}
